// One step of every move drag (docs/claude/snapping.md, *One move step*): the grid
// rule and the snap order, decided once for every kind dragged.
//  - The grid puts ONE point on the grid: the vertex grabbed, else the dragged
//    object's own reference point (`ObjectAdapter.origin`: a part's centre, a
//    shape's first vertex, a text box's anchor, an arc's centre). So an object
//    lands on the grid the same way dragged alone or with others — before, a
//    shape alone moved by grid steps, a group snapped its box's corner, and a part
//    its centre.
//  - Then a vertex lands on another object's vertex: the grabbed one, else the
//    nearest of the dragged objects' points (`vertexSnap`).
//  - With no vertex in reach and nothing grabbed, the drag's alignment applies
//    (`align`: the objects' centre on another's axis, a text box's edges, a
//    part's own axis snap).
// DOM-free: a drag hands in the stages it has.
import type { Vec2 } from '../shared/types';

/** A vertex landed on another object's: the offset that lands it, and where. */
export interface VertexLanding { dx: number; dy: number; at: Vec2 }

export interface MoveStepIn {
  /** The vertex grabbed, else the dragged object's own reference point. */
  ref: Vec2;
  /** Whether `ref` is a vertex grabbed (it, not the nearest point, then snaps,
   *  and no alignment follows). */
  grabbed: boolean;
  /** How far the cursor has gone since the press. */
  travel: Vec2;
  /** The cursor now. */
  cursor: Vec2;
  /** The grid's rounding of one coordinate (as it is when the grid is off). */
  grid: (v: number) => number;
  /** The dragged objects' points at an offset from where they started. */
  probes: (d: Vec2) => Vec2[];
  /** Lands a point among `moved` (the one at `probe`, else the nearest the cursor)
   *  on another object's vertex; null: none in reach. Null: the drag has none. */
  vertexSnap: ((moved: Vec2[], probe: Vec2) => VertexLanding | null) | null;
  /** The drag's alignment at an offset: the further offset it takes. */
  align?: ((d: Vec2) => Vec2) | null;
}

/** Where a move step puts the dragged objects: their offset from the start, and
 *  the vertex a snap landed on (for its marker). */
export interface MoveStepOut { d: Vec2; at: Vec2 | null }

/** The offset that puts `ref` on the grid after travelling `travel`. */
export function gridDelta(ref: Vec2, travel: Vec2, grid: (v: number) => number): Vec2 {
  return { x: grid(ref.x + travel.x) - ref.x, y: grid(ref.y + travel.y) - ref.y };
}

/** One step of a move drag: the grid, then a vertex landing, else alignment. */
export function moveStep(o: MoveStepIn): MoveStepOut {
  const d = gridDelta(o.ref, o.travel, o.grid);
  const probe = o.grabbed ? { x: o.ref.x + d.x, y: o.ref.y + d.y } : o.cursor;
  const snap = o.vertexSnap ? o.vertexSnap(o.probes(d), probe) : null;
  if (snap) return { d: { x: d.x + snap.dx, y: d.y + snap.dy }, at: snap.at };
  if (o.grabbed || !o.align) return { d, at: null };
  const a = o.align(d);
  return { d: { x: d.x + a.x, y: d.y + a.y }, at: null };
}
